/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { When } from "react-if";
import styled from "styled-components";
import {
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  Table,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  EventDetailResourceFragment,
  EventStatus,
  IncomingEventStatus,
  Resource,
} from "~/generated/graphql";
import { StyledTd } from "~/components/Tables/styles";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools";
import { getEventStatus } from "~/views/events/helpers";
import { OSCell } from "~/views/resources/components/OSCell";
import { NameWithLink } from "../Tables/cells/NameWithLink";

type ResourceItemProps = {
  resource?: EventDetailResourceFragment | Resource;
  status?: EventStatus | IncomingEventStatus;
  actions?: () => JSX.Element;
  highlight?: boolean;
};

export function ResourceItem({
  resource,
  status,
  actions,
  highlight,
}: ResourceItemProps) {
  if (!resource) {
    return null;
  }
  const os = {
    subType: resource?.os?.subType,
    type: resource?.os?.type,
  };

  return (
    <HighlightedTr $highlight={highlight}>
      <StyledTd css={{ width: "2%" }}>
        <When condition={isNotNil(status)}>
          <ExtendedTooltip caption={getEventStatus(status)?.description}>
            <SVGIcon
              name={getEventStatus(status)?.icon}
              color={getEventStatus(status)?.color()}
            />
          </ExtendedTooltip>
        </When>
      </StyledTd>
      <StyledTd css={{ width: "2%" }}>
        <OSCell os={os} />
      </StyledTd>
      <StyledTd css={{ width: "100%" }}>
        <FlexContainer direction="column" alignItems="flex-start">
          <NameWithLink
            name={resource?.name}
            link={`${ROUTES.resources.generateDetails(resource?.id ?? "")}`}
          />
          <Text
            css={{ wordBreak: "break-word" }}
            size="sm"
            color={theme.color.text.text02}
          >
            {resource?.id}
          </Text>
        </FlexContainer>
      </StyledTd>
      <StyledTd>{actions?.()}</StyledTd>
    </HighlightedTr>
  );
}

const HighlightedTr = styled(Table.tr)<{ $highlight?: boolean }>`
  background-color: ${({ $highlight }) =>
    $highlight ? theme.color.support.blueInverse : "inherit"};
`;
